import React from 'react'
import {
    Button,
    Container,
    Divider,
    Dropdown,
    DropdownDivider,
    DropdownItem,
    Form,
    Grid,
    Header,
    HeaderContent,
    HeaderSubheader,
    Icon,
    Input,
    Label,
    List,
    ListDescription,
    ListHeader,
    ListItem,
    Segment,
} from 'semantic-ui-react'

class Page extends React.Component {

    SERVER_STATES = {
        "wait": ["gray", "wait", "Waiting for your input..."],
        "eval": ["yellow", "circle notched loading", "Evaluating..."],
        "success": ["green", "checkmark", "Response received"],
        "error": ["red", "exclamation triangle", "Server is unreachable"]
    }

    SAMPLES = ["you're ae psychopath! i REALLY hope your know that...",
        "you're a niece person to talk with",
        "hello worlE i love graffe animal",
        "area you a communise??",
        "i like people who donate there money",
        "what is there name",
        "he red a book"
    ]

    SERVER_API = "https://spellchecker.muqs.it"

    constructor() {
        super()
        this.state = {
            userInput: "",
            serverOutputState: "wait",
            serverOutputValue: "",
            serverOutputTime: 0,
            serverOutputId: 0,
            userInputTask: null,
            dictLookupUserInput: "",
            dictLookupId: 0,
            dictLookupResponse: { "words": [], "n_query": 0, "n_words": 0, "t_eval": 0 },
        }
    }

    formatResponse(query, response) {
        const tokens = response["tokens"]
        const suggestions = response["suggestions"]

        let interactive = Array.from(tokens).fill(null)
        for (const [replacement, offset_begin, offset_end, index, score, error_t, dist] of suggestions) {
            if (interactive[index] === null) {
                interactive[index] = []
            }
            interactive[index].push([query.substring(offset_begin, offset_end), replacement, offset_begin, offset_end, score, error_t, dist])
        }

        // stitch tokens back to query
        let stitched = []
        for (let i = 0; i <= tokens.length; i++) {
            const prev = i === 0 ? null : tokens[i - 1]
            const curr = i === tokens.length ? null : tokens[i]
            const offset = prev === null ? 0 : prev[2]
            const end = curr === null ? query.length : curr[1]
            const word = query.substring(offset, end)
            if (word.length > 0) {
                stitched.push([word, offset, end])
            }
            if ((interactive[i] ?? null) !== null) {
                stitched.push(interactive[i])
            } else if (curr !== null) {
                stitched.push([query.substring(curr[1], curr[2]), curr[1], curr[2]])
            }
        }

        const result = []
        const stitched_current = []
        const stitched_suggestions = []
        for (let i = 0; i < stitched.length; i++) {
            let entry = stitched[i]
            let options = []
            let color
            let error_type
            let label
            if (typeof entry[0] !== "string") {
                if (entry[0][1] === "__MAGIC_STRING__") {
                    options = [<DropdownItem key={0} text={<i>No suggestions.</i>} />]
                    stitched_suggestions.push(entry[0][0])
                } else {
                    options = entry.map((value, index) => <DropdownItem
                        key={index}
                        text={value[1]}
                        description={<pre style={{ wordWrap: "break-word" }}>{value[6]} ED | {Number(value[4]).toFixed(2)}</pre>}
                        onClick={_ => {
                            const result = Array.from(stitched_current)
                            result[i] = value[1]
                            this.evaluate(result.join(""))
                        }}
                    />)
                    stitched_suggestions.push(entry[0][1])
                }
                stitched_current.push(entry[0][0])
                color = entry[0][5] === "r" ? "yellow" : "red"
                error_type = entry[0][5] === "r" ? "Real-word Error" : "Non-word Error"
                label = entry[0][0]
            } else {
                color = "lime"
                error_type = "Correct"
                label = entry[0]
                stitched_current.push(entry[0])
                stitched_suggestions.push(entry[0])
            }
            if (options.length > 0) {
                options.unshift(<DropdownDivider />)
            }
            entry = <Dropdown inline compact celled
                options={options}
                text={<span style={{
                    backgroundColor: color,
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    whiteSpace: "pre"
                }}>{label}</span>}
                icon={null}
                header={error_type}
            ></Dropdown>
            result.push(entry)
        }
        return <>
            {result}
            {suggestions.length > 0 ? this.renderSuggestion(query, stitched_suggestions) : ""}
        </>
    }

    renderSuggestion(query, suggestions) {
        const suggested_string = suggestions.join("")
        if (suggested_string === query) {
            return <></>
        }
        return <>
            <hr />
            <b>Did you mean:</b> <a href="/" onClick={e => {
                e.preventDefault()
                this.evaluate(suggested_string)
            }}><i>{suggested_string}</i></a>
        </>
    }

    evaluate(query) {
        if (this.state.userInputTask !== null) {
            clearInterval(this.state.userInputTask)
        }
        if (query.length === 0) {
            this.setState({ userInput: query, userInputTask: null, serverOutputId: 0, serverOutputState: "wait", serverOutputValue: "", serverOutputTime: 0 })
            return
        }
        const task = setTimeout(async () => {
            const serverOutputId = parseInt(Math.random() * 99999999)  // random ID to track every request we send. prevents previous output from being displayed due to server lag
            this.setState({ serverOutputState: "eval", serverOutputId: serverOutputId })
            let response
            try {
                response = await fetch(this.SERVER_API + "/eval", { method: "POST", body: query })
                response = await response.json()
            } catch (e) {
                if (this.state.serverOutputId === serverOutputId) {
                    this.setState({ serverOutputState: "error", serverOutputValue: e.message, serverOutputTime: 0 })
                }
                return
            }
            setTimeout(() => {
                if (this.state.serverOutputId === serverOutputId) {
                    this.setState({ serverOutputState: "success", serverOutputValue: this.formatResponse(query, response), serverOutputTime: Math.floor(response["t_eval"] * 1000) })
                }
            })
        }, 250)
        this.setState({ userInput: query, userInputTask: task, serverOutputState: "eval", serverOutputTime: 0 })
    }

    async lookup(query) {
        if (query.length === 0) {
            this.setState({ dictLookupUserInput: "", dictLookupId: 0, dictLookupResponse: { "words": [], "n_query": 0, "n_words": 0, "t_eval": 0 } })
            return
        }
        const dictLookupId = parseInt(Math.random() * 99999999)  // random ID to track every request we send. prevents previous output from being displayed due to server lag
        this.setState({ dictLookupUserInput: query, dictLookupId: dictLookupId })
        let response
        try {
            response = await fetch(this.SERVER_API + "/dict?" + (new URLSearchParams({ "query": query })))
            response = await response.json()
        } catch (e) {
            setTimeout(() => {
                if (this.state.dictLookupId === dictLookupId) {
                    this.setState({ dictLookupResponse: { "words": [], "n_query": 0, "n_words": 0, "t_eval": 0 }, dictLookupId: 0 })
                }
            })
            return
        }
        setTimeout(() => {
            if (this.state.dictLookupId === dictLookupId) {
                this.setState({ dictLookupResponse: response, dictLookupId: 0 })
            }
        })
    }

    render() {
        const [serverOutputStateColor, serverOutputStateIcon, serverOutputStateText] = this.SERVER_STATES[this.state.serverOutputState]
        return <div>
            <Grid centered container>
                <Grid.Column textAlign="center" width={15} style={{ marginTop: '2em' }}>
                    <Header as='h4' icon>
                        <Icon name="pencil" />
                        <HeaderContent>
                            Spelling Correction System
                            <HeaderSubheader>by Muqsit | TP061997</HeaderSubheader>
                        </HeaderContent>
                    </Header>
                </Grid.Column>
            </Grid>
            <Container style={{ marginTop: '3em' }}>
                <Grid celled stackable>
                    <Grid.Row>
                        <Grid.Column width={12}>
                            <Form>
                                <Form.TextArea
                                    placeholder="Text Editor (500 characters)"
                                    style={{ height: "24vh" }}
                                    value={this.state.userInput}
                                    onChange={e => this.evaluate(e.target.value)}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                />
                            </Form>
                            <Divider />
                            <Segment>
                                <Label size="tiny" attached="top" color={serverOutputStateColor} icon>
                                    <Icon name={serverOutputStateIcon} />
                                    {serverOutputStateText}
                                    {this.state.serverOutputTime > 0 ? <span style={{float: "right"}}>{this.state.serverOutputTime}ms</span> : <></>}
                                </Label>
                                <br />
                                {this.state.serverOutputValue}
                                {this.state.serverOutputState === "wait" ? <Button
                                    icon="random"
                                    labelPosition="left"
                                    content="Evaluate a sample sentence instead"
                                    size="mini"
                                    color="blue"
                                    onClick={_ => this.evaluate(this.SAMPLES[Math.round(Math.random() * this.SAMPLES.length)])}
                                /> : <></>}
                            </Segment>
                        </Grid.Column>
                        <Grid.Column width={4}>
                            <Header textAlign='center' as='h4'>
                                <Icon name="book" />
                                Dictionary
                            </Header>
                            <Divider />
                            <Form>
                                <Input fluid
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellcheck="false"
                                    icon={this.state.dictLookupId === 0 ? "search" : "circle notched loading"}
                                    onChange={e => this.lookup(e.target.value)}
                                    value={this.state.dictLookupUserInput}
                                    placeholder="Enter word or phrase" />
                            </Form>
                            <Segment style={{ height: "32vh", overflowY: "auto" }}>
                                <List divided celled style={{ wordWrap: "break-word" }}>
                                    {this.state.dictLookupResponse["words"].map((value, index) => <ListItem key={index}>
                                        <ListHeader>{value[0]}</ListHeader>
                                        <ListDescription>{value[1]} occurrence{value[1] === 1 ? "" : "s"}, {Math.log(value[2]).toFixed(2)} logprob</ListDescription>
                                    </ListItem>)}
                                    {this.state.dictLookupUserInput.length === 0 ? <><i>Dictionary results will appear here!</i></> : <></>}
                                </List>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
                <Segment>
                    This is a spelling correction system developed for my assignment for the module CT052-3-M-NLP (Natural Language Processing). It is built on unigrams, bigrams, and an edit distance variation
                    (Damerau-Levenshtein distance) with Optimal String Alignment approach.
                    <ul>
                        <li><span style={{ backgroundColor: "lime" }}>Green</span> highlighted words require no correction</li>
                        <li><span style={{ backgroundColor: "yellow" }}>Yellow</span> are real-word errors (click the word to view suggested candidates)</li>
                        <li><span style={{ backgroundColor: "red" }}>Red</span> are non-word errors (click the word to view suggested candidates)</li>
                    </ul>
                    <hr />
                    <Icon name="linkify" /><b>Dataset URL: </b><a href="https://www.kaggle.com/datasets/jannesklaas/scifi-stories-text-corpus">https://www.kaggle.com/datasets/jannesklaas/scifi-stories-text-corpus</a><br />
                    <Icon name="file word" /><b>Number of words: </b>26,505,698<br />
                    <Icon name="book" /><b>Vocabulary size: </b>227,711<br />
                    <Icon name="won" /><b>Number of bigrams: </b>4,888,625
                </Segment>
            </Container>
            <Segment basic></Segment>
        </div>
    }
}

export default Page